import { useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import type { Path, To } from 'react-router-dom';

/**
 * Hook to compute a new path from the current location and the given path
 * @returns A function that takes a path and returns a new path retaining white listed search params from the current location
 */
export const useGetEffectivePathToNavigate = (): ((to: To) => Partial<Path>) => {
    const location = useLocation();
    const locationRef = useRef(location);
    locationRef.current = location;

    const getEffectivePathToNavigate = useCallback((to: To): Partial<Path> => {
        let path: Partial<Path> = {};
        if (typeof to === 'string') {
            path =
                to.indexOf('?') > -1
                    ? {
                          pathname: to.slice(0, to.indexOf('?')),
                          search: to.slice(to.indexOf('?') + 1),
                      }
                    : {
                          pathname: to,
                      };
        } else {
            path = to;
        }

        const fullSearchParams = new URLSearchParams(locationRef.current.search);
        const newSearchParams = new URLSearchParams(path.search);
        newSearchParams.forEach((value: string, key: string) => {
            fullSearchParams.set(key, value);
        });
        path.search = fullSearchParams.toString();
        return path;
    }, []);

    return getEffectivePathToNavigate;
};
